/**
 * Twin materials resource (M8.7) — tenant-addable material classes + port
 * compatibility. Tier-1 (ungated). Replaces the hardcoded TS enum +
 * COMPATIBLE_WITH matrix in foundry-widgets/foundry-plant.ts (which stays as a
 * stub-mode fallback).
 *
 * Wire shapes mirror platform-core/internal/twin/materials.go exactly. An
 * empty org's GET falls back (server-side) to the foundry pack's classes, so
 * the list is never empty.
 */

import { api } from "@/lib/api-client";
import type { Paginated } from "./types";

export interface MaterialClass {
  class_id: string;
  display_name: string;
  color_token: string;
  pack: string;
  is_seed: boolean;
  notes: string;
  compatible_with: string[];
  created_at?: string;
  updated_at?: string;
}

export interface MaterialClassInput {
  class_id: string;
  display_name: string;
  color_token?: string;
  notes?: string;
  compatible_with?: string[];
}

export const twinMaterials = {
  list: () => api.get<Paginated<MaterialClass>>(`/v1/twin/materials`),
  /** Upsert a material class (and replace its compatibility edges if supplied). */
  upsert: (body: MaterialClassInput) =>
    api.post<MaterialClass>(`/v1/twin/materials`, body),
  /** Add a (symmetric, unless bidirectional=false) compatibility edge. 204. */
  addCompatibility: (
    classId: string,
    toClass: string,
    bidirectional = true,
  ) =>
    api.post<void>(
      `/v1/twin/materials/${encodeURIComponent(classId)}/compatibility`,
      { to_class: toClass, bidirectional },
    ),
  /** Delete a class. 409 if still referenced by a material lot. 204 on success. */
  remove: (classId: string) =>
    api.delete<void>(`/v1/twin/materials/${encodeURIComponent(classId)}`),
};
